/**
 * The founder's own logo, and the control to replace it.
 *
 * Everything shown before this was DERIVED — extracted from a pitch deck or
 * lifted from a favicon — and a founder had no way to correct it. There was not
 * even a column to write to. `logo_source = 'founder'` is now the authoritative
 * tier; this is where a founder reaches it.
 *
 * Renders the logo when there is one, initials when there is not, and — only in
 * edit mode — a camera affordance. A logo that came from a guess is labelled as
 * such, because "we extracted this from your deck" and "this is your logo" are
 * different claims and the founder is the one who can tell them apart.
 */

import React, { useState } from 'react';
import { View, Text, Image, ActivityIndicator } from 'react-native';
import { Camera } from 'lucide-react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useThemeColors } from '@/lib/useThemeColors';
import { useAlert } from '@/components/AlertDialog';
import { handleError } from '@/lib/errors';
import { usePickLogo, useSetCompanyLogo } from '@/lib/company/use-set-company-logo';

interface Props {
  startupId: string | null;
  companyName: string;
  logoUrl?: string | null;
  /** 'founder' means the founder chose it; anything else is our guess. */
  logoSource?: 'founder' | 'deck' | 'website' | null;
  isKnockout?: boolean;
  isEditing: boolean;
}

export function CompanyLogoEditor({
  startupId,
  companyName,
  logoUrl,
  logoSource,
  isKnockout = false,
  isEditing,
}: Props) {
  const colors = useThemeColors();
  const { error: showError, success } = useAlert();
  const { pick } = usePickLogo();
  const setLogo = useSetCompanyLogo();
  const [busy, setBusy] = useState(false);

  const initials = companyName
    .split(' ')
    .map((n) => n[0])
    .filter(Boolean)
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const handlePick = async () => {
    if (!startupId) {
      showError('Profile Loading', 'Your company profile is still loading. Try again in a moment.');
      return;
    }
    setBusy(true);
    try {
      const uri = await pick();
      // Cancelling is not a failure — leave silently rather than showing an error.
      if (!uri) return;
      await setLogo.mutateAsync({ startupId, uri });
      success('Logo Updated', 'Your logo is now shown everywhere your company appears.');
    } catch (err) {
      const { message, isCancelled } = handleError(err, { context: 'CompanyLogoEditor.handlePick' });
      if (!isCancelled) showError('Could not update logo', message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <View className="items-center mb-4">
      <View
        className="w-20 h-20 rounded-2xl items-center justify-center overflow-hidden"
        style={{
          // A knockout logo is white-on-transparent and vanishes on the tinted
          // chip; the dark backdrop is what keeps it visible.
          backgroundColor: isKnockout ? colors.dark[800] : colors.primary[100],
        }}
      >
        {logoUrl ? (
          <Image
            source={{ uri: logoUrl }}
            // `contain`, not `cover`: a logo cropped to fill loses its wordmark.
            resizeMode="contain"
            style={{ width: 80, height: 80 }}
            accessibilityLabel={`${companyName} logo`}
          />
        ) : (
          <Text style={{ color: colors.primary[600] }} className="text-3xl font-bold">
            {initials}
          </Text>
        )}

        {busy && (
          <View
            className="absolute inset-0 items-center justify-center"
            style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
          >
            <ActivityIndicator size="small" color="#FFFFFF" />
          </View>
        )}
      </View>

      {isEditing && (
        <View
          className="rounded-xl overflow-hidden mt-3"
          style={{ height: 36, backgroundColor: colors.primary[600] }}
        >
          {/* Dimensions on the wrapper: a callback `style` on Pressable does not
              compute height reliably on RN 0.81+. */}
          <AnimatedPressable
            onPress={handlePick}
            disabled={busy}
            accessibilityRole="button"
            accessibilityLabel={logoUrl ? 'Replace company logo' : 'Upload company logo'}
            className="flex-1 px-4 flex-row items-center justify-center gap-2"
          >
            <Camera size={15} color="white" />
            <Text className="text-white font-medium text-sm" numberOfLines={1}>
              {logoUrl ? 'Replace logo' : 'Upload logo'}
            </Text>
          </AnimatedPressable>
        </View>
      )}

      {/* Say where it came from. A founder cannot correct a guess they were
          never told was a guess — which is how a photo of a person sat on a
          company's card unreported. */}
      {isEditing && logoUrl && logoSource && logoSource !== 'founder' && (
        <Text
          style={{ color: colors.text.tertiary }}
          className="text-xs mt-2 text-center"
          numberOfLines={2}
        >
          {logoSource === 'deck'
            ? 'Taken from your pitch deck — replace it if this is not your logo.'
            : 'Taken from your website — replace it if this is not your logo.'}
        </Text>
      )}
    </View>
  );
}
